import React, { useCallback, useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AppFrame } from './AppFrame';
import { PAGE_BY_SLUG } from './pages';
import { can, useSession, useSignOut } from './session';
import { usePrefs, type Theme, type Density } from './prefs';
import { NoAccessPage, NotFoundPage, LockedScreen } from './states';
import { LicenceCtx, UNKNOWN_SERVER, useLicenceStatus, useLiveAlerts, useShellStatus } from './shell/data';
import { LiveToasts } from './shell/Toasts';
import { WhatsNewModal } from './shell/WhatsNewModal';
import { useActionSync } from './shell/actionSync';
import { SideNav, TopBar } from '../components';
import { api, call, onApiError } from '../api/client';
import { realtime } from '../realtime/client';
import { t, useI18n } from '../i18n';
import { ShellCtx } from './pageKit';
import { LiveNavCtx } from '../ds/liveNav';
import { date } from '../lib/format';
import { roleName } from '../lib/names';

const fmtDate = (iso?: string | null) => (iso ? date(iso, undefined, false) : '');

// The session whose profile was applied. It lives outside the component because a language change
// remounts the shell, and applying the profile again would undo that change.
let appliedSession: string | null = null;

/**
 * App shell (SHL-01): TopBar with the MT5 status pill, ⌘K search, alert drawer and user menu;
 * licence and paused banners; SideNav filtered by the session's permissions with live counts;
 * the page (or the no-access / 404 state); global toasts and the What's New modal.
 */
export function Shell({ notFound }: { notFound?: boolean }) {
  const { session, loading } = useSession();
  const loc = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const signOut = useSignOut();
  const { theme, setTheme, density, setDensity, sound, setSound } = usePrefs();
  const { code, languages, setLanguage } = useI18n();
  const signedIn = !!session;
  const licence = useLicenceStatus(true);
  const shell = useShellStatus(signedIn);
  const alerts = useLiveAlerts(signedIn && can(session, 'page.alerts'));
  useActionSync(signedIn && can(session, 'page.cases'), shell.data?.server_time);
  const page = PAGE_BY_SLUG[loc.pathname.replace(/^\//, '')];

  // signed out by the API (401) or the gateway (4401): back to sign-in
  useEffect(() => onApiError((e) => e.status === 401 && qc.setQueryData(['session'], null)), [qc]);
  useEffect(() => realtime.onUnauthenticated(() => qc.setQueryData(['session'], null)), [qc]);
  useEffect(() => {
    if (signedIn) realtime.connect();
  }, [signedIn, session?.user.id]);

  // the profile's language and display preferences apply once per session
  useEffect(() => {
    if (!session || appliedSession === session.session.id) return;
    appliedSession = session.session.id;
    const p = session.user.preferences as { theme?: Theme; density?: Density; sound?: boolean } | undefined;
    if (p?.theme) setTheme(p.theme);
    if (p?.density) setDensity(p.density);
    if (typeof p?.sound === 'boolean') setSound(p.sound);
    if (session.user.language && session.user.language !== code) void setLanguage(session.user.language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const savePref = useCallback((patch: Record<string, unknown>) => {
    void call(api.PATCH('/me', { body: patch as any })).catch(() => {});
  }, []);

  // GET /search; a pasted client token (ACC- / EML- / PHN- / IP- + 12 characters) answers its account hit(s)
  // and `token: {kind, found}`; the TopBar labels the hits by the token kind and shows the not-found state
  const onSearch = useCallback(async (q: string) => {
    if (!q.trim()) return [];
    const r = await call(api.GET('/search', { params: { query: { q } } }));
    const g = (kind: string, label: string, items: any[] | undefined) => ({
      kind,
      label,
      items: (items || []).map((h) => ({ id: h.id, label: h.label, sublabel: h.sublabel, path: h.path })),
    });
    return {
      groups: [
        g('account', t('app.search.accounts', 'Accounts'), r.accounts),
        g('symbol', t('app.search.symbols', 'Symbols'), r.symbols),
        g('group', t('app.search.groups', 'Groups'), r.groups),
        g('case', t('app.search.cases', 'Cases'), r.cases),
      ],
      token: r.token ?? null,
    };
  }, []);

  const ackAll = useCallback(
    async (ids: string[]) => {
      if (!ids.length) return;
      await call(api.POST('/alerts/acknowledge', { body: { ids } })).catch(() => {});
      await qc.invalidateQueries({ queryKey: ['alerts'] });
      await qc.invalidateQueries({ queryKey: ['shell'] });
    },
    [qc],
  );

  if (loading) return <AppFrame>{null}</AppFrame>;
  if (!session) {
    if (licence.locked) return <LockedScreen validUntil={licence.validUntil} session={session} onUnlocked={() => location.reload()} />;
    return <Navigate to={'/login?next=' + encodeURIComponent(loc.pathname + loc.search)} replace />;
  }
  if (licence.locked && !(session.user.is_launcher && page?.id === 'LCH-03'))
    return <LockedScreen validUntil={licence.validUntil} session={session} onUnlocked={() => location.reload()} />;
  if (session.user.welcome_required) return <Navigate to="/welcome" replace />;

  const sh = shell.data;
  const counts = sh?.counts;
  const banner = sh?.licence ?? licence.banner;
  const allowed = !page?.perm || can(session, page.perm);
  const content = notFound ? (
    <NotFoundPage path={loc.pathname} />
  ) : !allowed && page ? (
    <NoAccessPage page={page} session={session} />
  ) : (
    <Outlet />
  );

  return (
    <LicenceCtx.Provider value={licence}>
      <ShellCtx.Provider value={sh}>
        <LiveNavCtx.Provider value={{ permissions: session.permissions, counts: counts || {} }}>
          <AppFrame>
            <TopBar
              server={sh?.server ?? UNKNOWN_SERVER}
              user={{ name: session.user.name, email: session.user.email, role: roleName(session.roles?.[0]) }}
              counts={{
                alerts_open: counts?.alerts_open ?? alerts.counters?.open ?? 0,
                alerts_critical: counts?.alerts_critical ?? alerts.counters?.critical ?? 0,
              }}
              licenceBanner={
                banner ? { ...banner, date: fmtDate(banner.kind === 'expired_grace' ? licence.graceEndsAt : licence.validUntil) } : null
              }
              languages={languages}
              lang={code}
              onLang={(c: string) => {
                void setLanguage(c);
                savePref({ language: c });
              }}
              theme={theme}
              onTheme={(v: Theme) => {
                setTheme(v);
                savePref({ preferences: { theme: v, density, sound } });
              }}
              sound={sound}
              onSound={(v: boolean) => {
                setSound(v);
                savePref({ preferences: { theme, density, sound: v } });
              }}
              onSearch={onSearch}
              alertItems={alerts.items}
              onAckAll={ackAll}
              onNavigate={(path: string) => navigate(path)}
              onSignOut={async () => {
                await signOut();
                navigate('/login');
              }}
            />
            <div className="bx-body">
              <div className="bx-navcol">
                <SideNav
                  active={page?.nav || ''}
                  permissions={session.permissions}
                  cases={counts?.cases_open ?? 0}
                  alerts={counts?.alerts_open ?? alerts.counters?.open ?? 0}
                  news={counts?.whats_new_unread ?? 0}
                />
              </div>
              {content}
            </div>
            <LiveToasts enabled={can(session, 'page.alerts')} />
            <WhatsNewModal enabled={can(session, 'page.whats_new')} session={session?.session.id} />
          </AppFrame>
        </LiveNavCtx.Provider>
      </ShellCtx.Provider>
    </LicenceCtx.Provider>
  );
}
